<script setup lang="ts">
import { PropType, ref } from "vue";
import { Trainer } from "../../types/PersologOnlineTypes.ts";
import { useConfirm } from "primevue/useconfirm";
import { ProfileAdministrationAPI } from "../../api";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { bringFocusToElementById, isMobileView } from "../../utils/GeneralUtils.ts";

const props = defineProps({
    trainer: {
        type: Object as PropType<Trainer>,
        required: true,
    },
});

const { t } = useI18n();

const confirm = useConfirm();
const loading = ref(false);
const router = useRouter();

function handleAccountDeletionDialog() {
    confirm.require({
        group: "accountDeletion",
        message: t("trainer-settings.account.modal.message"),
        header: t("trainer-settings.account.modal.heading"),
        accept: async () => {
            loading.value = true;
            // profile-administration deletes all projects and participants with their data, through
            // profile-contractor (RDT-470).
            await ProfileAdministrationAPI.deleteAccount(props.trainer.id);
            loading.value = false;
            await router.push("/products");
            globalThis.location.reload();
        },
    });
}

/**
 * Accessibility: Brings focus to the delete button in the dialog when it is shown.
 */
function bringFocusToDialog() {
    bringFocusToElementById(`acc-deletion-cancel_btn`);
}
</script>

<template>
    <Button
        class="mr-2"
        type="button"
        :label="t('trainer-settings.account.deleteButton')"
        text
        severity="danger"
        :icon="loading ? 'pi pi-spin pi-spinner' : 'pi pi-trash'"
        @click="handleAccountDeletionDialog()"
    />
    <ConfirmDialog
        group="accountDeletion"
        :style="{ width: isMobileView() ? '100%' : '50%' }"
        @show="bringFocusToDialog()"
    >
        <template #container="{ message, acceptCallback, rejectCallback }">
            <div class="flex flex-col items-center p-8 bg-surface rounded">
                <div
                    class="rounded-full bg-brand text-white inline-flex justify-center items-center h-24 w-24 -mt-20"
                >
                    <i class="pi pi-exclamation-triangle text-5xl"></i>
                </div>
                <span class="font-bold text-2xl block mb-2 mt-6">{{ message.header }}</span>
                <p class="mb-0" v-html="message.message" />
                <div class="flex items-center gap-2 mt-6">
                    <Button :label="t('buttons.delete')" @click="acceptCallback" class="w-32"></Button>
                    <Button :id="`acc-deletion-cancel_btn`" :label="t('buttons.cancel')" outlined @click="rejectCallback" class="w-32"></Button>
                </div>
            </div>
        </template>
    </ConfirmDialog>
</template>
